import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, NavLinkProps } from "react-router-dom";

import { useTranslation } from "@/core/i18n";

import { ReactComponent as LeftArrow } from "atoms/icons/chevron_left.svg";
import { ReactComponent as RightArrow } from "atoms/icons/chevron_right.svg";

type Props = {
  items: (NavLinkProps & {
    hide?: boolean;
    dataTestId?: string;
    showIndicator?: boolean;
  })[];
  /**
   * `default`/`v3` render the classic pill buttons; `underline` renders quiet
   * text tabs on a shared baseline with a colored underline on the active tab.
   */
  variant?: "default" | "v3" | "underline";
  /**
   * "compact" tightens pill gap/padding so wide tab sets fit on desktop without
   * scrolling. Only applies to the pill variants — `underline` carries its own
   * spacing, so density is ignored there.
   */
  density?: "default" | "compact";
};

const SCROLL_THRESHOLD = 50;

export const NavLinkTabs = ({ items, variant = "default", density = "default" }: Props) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isStartVisible, setIsStartVisible] = useState(false);
  const [isEndVisible, setIsEndVisible] = useState(false);

  const getActiveStyles = () => {
    return "items-center justify-center border border-primary bg-primary font-bold text-white hover:border-primary hover:bg-primary-hover hover:text-white";
  };

  const getInactiveStyles = () => {
    return "items-center justify-center border border-border bg-white text-primary hover:border-border hover:bg-white/90";
  };

  const scrollToStart = () => {
    scrollContainerRef.current?.scrollTo({ left: 0, behavior: "smooth" });
  };

  const scrollToEnd = () => {
    scrollContainerRef.current?.scrollTo({
      left: scrollContainerRef.current.scrollWidth,
      behavior: "smooth",
    });
  };

  const checkVisibility = useCallback(() => {
    const currentRef = scrollContainerRef.current;
    if (!currentRef) {
      return;
    }

    // If the scrollLeft value is greater than the threshold, it means the start of the list is not fully visible
    setIsStartVisible(currentRef.scrollLeft > SCROLL_THRESHOLD);

    // If the difference between the scrollWidth (total width) and the sum of scrollLeft (scrolled width) and clientWidth (visible width)
    // is greater than the threshold, it means the end of the list is not fully visible
    setIsEndVisible(
      currentRef.scrollWidth - currentRef.scrollLeft - currentRef.clientWidth > SCROLL_THRESHOLD,
    );
  }, []);

  // Check for visibility of arrows on scroll
  useEffect(() => {
    const currentRef = scrollContainerRef.current;
    if (!currentRef) {
      return;
    }

    checkVisibility();
    currentRef.addEventListener("scroll", checkVisibility);

    return () => {
      currentRef.removeEventListener("scroll", checkVisibility);
    };
  }, [checkVisibility]);

  // Check for visibility of arrows on resize
  useEffect(() => {
    const debounce = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        checkVisibility();
      }, 200);
    };

    globalThis.addEventListener("resize", debounce);

    return () => {
      globalThis.removeEventListener("resize", debounce);

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [checkVisibility]);

  return (
    <div className="relative">
      {/*
        Rendered always and faded with the same `data-closed` contract the shared Transition
        wrapper used, rather than mounting and unmounting: the arrow keeps its fade-out, and the
        hidden state is taken out of the tab order and made unclickable so an invisible button is
        never reachable.
      */}
      <button
        aria-hidden={!isStartVisible}
        aria-label={t("common.scrollTabsToStart")}
        className="absolute top-0 left-0 flex h-9 w-16 cursor-pointer items-center bg-linear-to-r from-background transition-opacity duration-150 data-closed:pointer-events-none data-closed:opacity-0"
        data-closed={isStartVisible ? undefined : ""}
        tabIndex={isStartVisible ? undefined : -1}
        type="button"
        onClick={scrollToStart}
      >
        <LeftArrow className="h-6 w-6 fill-current" />
      </button>

      <div
        className={cx(
          "flex shrink-0 flex-row overflow-auto empty:hidden",
          variant === "underline"
            ? "gap-6 border-b border-border sm:gap-8"
            : cx(
                "py-1 font-bold text-dark-grey",
                density === "compact" ? "gap-2" : "gap-3 sm:gap-4 lg:gap-6",
              ),
        )}
        ref={scrollContainerRef}
      >
        {/* Not reorderable, index is fine as 'key' */}
        {items.map(({ hide, dataTestId = "navLink", showIndicator, children, ...itemProps }) => (
          <NavLink
            className={({ isActive }) =>
              variant === "underline"
                ? cx(
                    "-mb-px flex items-center border-b-2 px-1 pt-1 pb-2 type-body-md font-medium whitespace-nowrap capitalize transition-colors",
                    isActive
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
                    hide && "hidden",
                  )
                : cx(
                    "flex items-center rounded-md py-1.5 text-tabs whitespace-nowrap capitalize transition-colors",
                    density === "compact" ? "px-4" : "px-6",
                    isActive ? getActiveStyles() : getInactiveStyles(),
                    hide && "hidden",
                  )
            }
            data-testid={dataTestId}
            key={getNavItemKey(itemProps.to, dataTestId)}
            {...itemProps}
          >
            <>
              {showIndicator && (
                <div className="mr-2 h-[5px] w-[5px] rounded-md bg-[rgb(var(--nav-v3-indicator))]" />
              )}
              {children}
            </>
          </NavLink>
        ))}
      </div>

      <button
        aria-hidden={!isEndVisible}
        aria-label={t("common.scrollTabsToEnd")}
        className="absolute top-0 right-0 flex h-9 w-16 cursor-pointer items-center justify-end bg-linear-to-l from-background transition-opacity duration-150 data-closed:pointer-events-none data-closed:opacity-0"
        data-closed={isEndVisible ? undefined : ""}
        tabIndex={isEndVisible ? undefined : -1}
        type="button"
        onClick={scrollToEnd}
      >
        <RightArrow className="h-6 w-6 fill-current" />
      </button>
    </div>
  );
};

const getNavItemKey = (to: NavLinkProps["to"], dataTestId: string) => {
  if (typeof to === "string") {
    return `${dataTestId}-${to}`;
  }

  return `${dataTestId}-${to.pathname ?? ""}-${to.search ?? ""}-${to.hash ?? ""}`;
};
